import React, { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsTestUser } from '../../../hooks/useIsTestUser';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  styled,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tier3Provider } from '../../../data/models/provider.model';
import * as providerSlice from '../../../data/provider.slice';
import * as referenceSlice from '../../../data/reference.slice';
import providerUtils from '../../../services/providerUtils';
import DialogTransition from '../DialogTransition';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import ChipSelectMenu from '../../form/ChipSelectMenu';
import CheckboxInput from '../../form/CheckboxInput';
import PhoneInput from '../../form/PhoneInput';
import { TransportationType } from '../../../data/models/transportation-type.model';
import _ from 'lodash';
import validationUtils from '../../../services/validationUtils';

const PREFIX = 'Tier3ProviderDialog';

const classes = {
  form: `${PREFIX}-form`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface Tier3ProviderDialogProps {
  title: string;
  open: boolean;
  provider?: Tier3Provider;
  close: (saved: boolean) => void;
}

export default function Tier3ProviderDialog({
  title,
  open,
  provider,
  close,
}: Tier3ProviderDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();
  const isTestUser = useIsTestUser();

  const states = useAppSelector(referenceSlice.selectStateCodes);
  const tripModes = useAppSelector(referenceSlice.selectTripModes);
  const paymentMethods = useAppSelector(referenceSlice.selectPaymentTypes);
  const coverageAreas = useAppSelector(providerSlice.selectCoverageAreas);
  const transportationTypes = useAppSelector(
    referenceSlice.selectTransortationTypes,
  );

  const saving = useAppSelector(providerSlice.selectSavingProvider);
  const saved = useAppSelector(providerSlice.selectSavedProvider);
  // const savingError = useAppSelector(providerSlice.selectSavingError);

  React.useEffect(() => {
    dispatch(referenceSlice.fetchStateCodesAsync());
    dispatch(referenceSlice.fetchTripModesAsync());
    dispatch(referenceSlice.fetchPaymentTypesAsync());
  }, []);

  React.useEffect(() => {
    if (!saving && saved) {
      closeDialog(true);
      dispatch(providerSlice.setSavingProvider(false));
    }
  }, [saving, saved]);

  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues: providerUtils.getTier3Defaults(provider),
    mode: 'onTouched',
  });

  useEffect(() => {
    reset(providerUtils.getTier3Defaults(provider));
  }, [provider]);

  const closeDialog = (saved: boolean) => {
    reset();
    close(saved);
  };

  const getTransportTypeID = (types: TransportationType[], bc: string) => {
    const ttype = types.find((t) => t.transportationTypeBC === bc);
    // This should always exist but I just hard code to 4 in case it
    // doesn't because that should always be the ID.
    return ttype?.transportationTypeID || 4;
  };

  const saveProvider = React.useCallback(
    (data: any) => {
      const transportTypeID = getTransportTypeID(
        transportationTypes,
        'EXTERNAL',
      );
      const p = providerUtils.getTier3ProviderFromData(
        isTestUser,
        provider?.transportationProviderID,
        data,
        coverageAreas,
        transportTypeID,
        provider?.isInternal,
      );
      const id = provider?.transportationProviderID;
      const fn = id
        ? providerSlice.updateTier3ProviderAsync(p)
        : providerSlice.createTier3ProviderAsync(p);
      dispatch(fn);
    },
    [dispatch, provider, isTestUser],
  );

  return (
    <StyledDialog
      fullScreen={false}
      open={open}
      onClose={close}
      fullWidth={true}
      maxWidth="sm"
      TransitionComponent={DialogTransition}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box className={classes.form}>
          <TextInput
            label={t('provider.name')}
            name="Name"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 100 }}
          />
          <TextInput
            label={t('provider.contactFirst')}
            name="ContactFirstName"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 100 }}
          />
          <TextInput
            label={t('provider.contactLast')}
            name="ContactLastName"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 100 }}
          />
          <TextInput
            label={t('provider.contactEmail')}
            name="ContactEmail"
            type="email"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              pattern: validationUtils.emailValidation(),
            }}
            inputProps={{ maxLength: 100 }}
          />
          <PhoneInput
            label={t('provider.contactPhone')}
            name="ContactPhone"
            extensionName="ContactPhoneExtension"
            required={true}
            control={control as any}
            errors={formState.errors}
          />
          <TextInput
            label={t('provider.city')}
            name="City"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 100 }}
          />
          <SelectMenu
            label={t('provider.state')}
            name="State"
            values={states}
            valueAttr="stateCode"
            labelAttr="stateName"
            required={true}
            control={control as any}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
          <ChipSelectMenu
            label={t('provider.tripModes')}
            name="TripModes"
            values={tripModes}
            valueAttr="tripModeID"
            labelAttr="tripModeName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
            variant="filled"
          />
          <ChipSelectMenu
            label={t('provider.paymentMethods')}
            name="PaymentMethods"
            values={paymentMethods}
            valueAttr="paymentTypeID"
            labelAttr="paymentTypeName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
            variant="filled"
          />
          <ChipSelectMenu
            label={t('provider.locations')}
            name="CoverageAreas"
            values={coverageAreas}
            valueAttr="coverageAreaID"
            labelAttr="coverageAreaName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
            variant="filled"
          />
          <TextInput
            label={t('provider.capacity')}
            name="Capacity"
            required={false}
            control={control as any}
            inputProps={{ type: 'number', min: 0, max: 10000 }}
          />
          <CheckboxInput
            label={t('provider.clearToTransport')}
            name="IsClearToTransport"
            required={false}
            control={control}
          />
          <CheckboxInput
            label={t('provider.isActive')}
            name="IsActive"
            required={false}
            control={control}
          />
          <CheckboxInput
            label={t('provider.isInternal')}
            name="IsInternal"
            required={false}
            control={control}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(saveProvider)}
          disabled={!formState.isValid || saving}>
          {t('common.save')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => closeDialog(false)}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
